/** @jsx jsx */
// eslint-disable-next-line
import React, { Component } from 'react'
import { css, jsx } from '@emotion/core'
import * as Paddle from '@paddle/paddle-js'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import sessionService from '../../services/session'
import tokenService from '../../services/token'
import { history } from '../App'
import ActionTypes from '../../constants/actionTypes'
import * as Constants from '../../constants'
import LoginForm from './components/LoginForm'
import getErrorMessage from '../../utils/getErrorMessage'
import zendeskWidget from '../../utils/zendeskWidget'
import amplitude from '../../analytics/amplitude'
import ZendeskWidgetButton from '../components/ZendeskWidgetButton'
import { showLicenceReminder } from '../admin/actions'
import getIp from '../../utils/getIp'
import MetricaLogo from '../../assets/logo.png'
import LanguageSelect from './components/LanguageSelect'

const styles = {}
styles.container = css`
  height: 100%;
  width: 100%;
`
styles.loginBox = css`
  width: 500px;
  margin: 0 auto 20px auto;
  background: var(--metrica-green-black-gradient);
  border: 1px solid var(--metrica-green);
  border-radius: 8px;
  align-items: center;
  padding: 40px 50px;
`
styles.title = css`
  text-align: center;
  color: white;
  margin: 0 0 60px 0;
  font-size: 30px;
`
styles.goToLink = css`
  cursor: pointer;
  color: white;
  font-size: 13px;
`
styles.linksContainer = css`
  width: 500px;
  margin: 0 auto;
`

class Login extends Component {
  componentDidMount() {
    if (window.zESettings) {
      zendeskWidget.reset()
    } else {
      zendeskWidget.setup()
    }

    // Initialize Paddle in Login page for ProfitWell Retain, to not need to be logged in.
    const isProduction = process.env.REACT_APP_ENV === 'production'
    const pwAuth = process.env.REACT_APP_PROFITWELL_TOKEN || null
    Paddle.initializePaddle({
      environment: isProduction ? 'production' : 'sandbox',
      token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
      pwAuth
    })
  }

  onSubmit = async (values, { setSubmitting }) => {
    zendeskWidget.hide()
    try {
      const response = await sessionService.create(values.email, values.password)

      const data = response.data
      try {
        data.userIp = await getIp()
      } catch (error) {
        console.error(error)
      }

      const token = data.token
      const user = data.user
      const subscriptionInUse = data.subscriptionInUse
      tokenService.set(token, user._id)
      this.props.dispatch({ type: ActionTypes.MAKE_AUTH, payload: data })

      amplitude.setUser(data.user)
      amplitude.logEvent(Constants.AnalyticsEvents.LOGIN_METRICA_CLOUD)
      showLicenceReminder(user, subscriptionInUse)

      // Redirect to the path user was trying to access before auth.
      const location = this.props.location
      const from = location && location.state && location.state.from
      const redirectUrl = (from && `${from.pathname}${from.search}`) || '/'
      history.replace(redirectUrl)

      zendeskWidget.hide()
    } catch (error) {
      console.error(error)
      const errorMsg = getErrorMessage(error)

      // We are handling this error on the api service file
      const apiMessages = Constants.API_MESSAGES
      if (errorMsg === apiMessages.trialLicenseInvalid || errorMsg === apiMessages.licenseInvalid) {
        return
      }

      const wrongPassMsg = 'Wrong username and/or password'
      if (errorMsg !== wrongPassMsg) {
        zendeskWidget.openInForm()
      }

      toast.error(errorMsg)
    } finally {
      setSubmitting(false)
    }
  }

  goToReset = () => {
    history.push('/forgot-password')
  }

  goToSignUp = () => {
    const isProduction = process.env.REACT_APP_ENV === 'production'
    const baseUrl = isProduction
      ? 'https://getplay.metrica-sports.com'
      : 'http://getplay-dev.metrica-sports.com'

    const location = this.props.location
    const from = location && location.state && location.state.from
    const returnUrl = (from && `${window.location.origin}${from.pathname}${from.search}`) || ''
    const signUpUrl = `${baseUrl}${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`

    window.open(signUpUrl, '_self')
  }

  render() {
    return (
      <React.Fragment>
        <div css={styles.container}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '20px 65px 20px 20px',
              color: 'white',
              height: '90px',
              alignItems: 'center'
            }}
          >
            <a href="http://metrica-sports.com">
              <img
                style={{ width: '165px', height: 'auto', marginLeft: '40px' }}
                src={MetricaLogo}
                alt="Metrica Logo"
              />
            </a>
            <LanguageSelect />
            <ZendeskWidgetButton />
          </div>

          <div style={{ marginTop: '100px' }}>
            {/* TODO: Add translation */}
            <h1 css={styles.title}>Login</h1>

            <div css={styles.loginBox}>
              <LoginForm
                onSubmit={this.onSubmit}
                goToReset={this.goToReset}
                goToSignUp={this.goToSignUp}
              />
            </div>
            <div css={styles.linksContainer}>
              <div
                css={styles.goToLink}
                onClick={() => this.goToReset()}
                dangerouslySetInnerHTML={{
                  __html: window.I18n.t('login.forgotPassword', {
                    pre: '<a style="text-decoration: underline;">',
                    post: '</a>'
                  })
                }}
              />

              <div
                css={styles.goToLink}
                style={{ marginTop: '8px' }}
                onClick={() => this.goToSignUp()}
                dangerouslySetInnerHTML={{
                  __html: window.I18n.t('login.dontHaveAccount', {
                    pre: '<a style="text-decoration: underline;">',
                    post: '</a>'
                  })
                }}
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }
}

Login.propTypes = {
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired
}

export default connect()(Login)
